import axios from 'axios'
import { Serializer } from 'jsonapi-serializer'
import Inflector from 'inflected'
import cloneDeep from 'lodash/cloneDeep'
import includes from 'lodash/includes'
import { instance as store } from 'app/store'

const DEFER = 0x00

/**
 * A function that accepts an object and, for each property converts it from camel case to kebab case. Example:
 * { multiWordKey: 'value' } becomes { multi-word-key: 'value'}.
 *
 * Convenience Links:
 * {@link https://www.npmjs.com/package/inflected#inflectordasherize Inflector dasherize} and
 * {@link https://www.npmjs.com/package/inflected#inflectorunderscore Inflector underscore} for more details.
 *
 * @function inflect
 * @param {Object} obj - The object whose keys are being converted from camel case to kebab case
 * @returns {Object} - A copy of obj with all key names updated
 */
const inflect = (obj) => {
  const val = {}

  Object.keys(obj || {}).forEach((key) => {
    val[Inflector.dasherize(Inflector.underscore(key))] = obj[key]
  })

  return val
}

/**
 * An object whose methods return a JSON object suitable for the data portion of an Axios request.
 *
 * Convenience Links:
 * {@link https://www.npmjs.com/package/jsonapi-serializer Serializer} for more details.
 */
const bodyProcessors = {
  post({ type, data, meta }) {
    const serializer = new Serializer(type, {
      pluralizeType: false,
      attributes: Object.keys(data),
      meta: inflect(meta)
    })

    return JSON.stringify(serializer.serialize(data))
  },

  put({ type, changes, record, meta }) {
    const newRecord = { ...cloneDeep(record), ...changes }

    const serializer = new Serializer(type, {
      pluralizeType: false,
      attributes: Object.keys(newRecord),
      meta: inflect(meta)
    })

    return JSON.stringify(serializer.serialize(newRecord))
  },

  patch({ type, changes, record, meta }) {
    const newRecord = { ...cloneDeep(record), ...changes }

    const serializer = new Serializer(type, {
      pluralizeType: false,
      attributes: Object.keys(changes),
      meta: inflect(meta)
    })

    return JSON.stringify(serializer.serialize(newRecord))
  },

  delete({ meta }) {
    if (!meta || Object.keys(meta).length === 0) {
      return ''
    }

    return JSON.stringify({ meta })
  }
}

/**
 * This function uses axios to make a call to a back-end RESTful API
 *
 * Convenience Links:
 * {@link https://axios-http.com/docs/req_config Axios Request Configuration}
 * {@link https://axios-http.com/docs/res_schema Response Schema}
 *
 * @function
 * @param {Object} param0 - The object that will be transformed into an RESTful API call
 * @param {string} param0.url - The server URL that will be used for the request
 * @param {Object} param0.params - URL parameters sent with the request
 * @param {string} param0.method - The RESTful request method to be used when making the request
 * @param {string} param0.type - A name representing the data type
 * @param {Object} param0.data - The data being sent in the request
 * @param {Object} param0.changes - The updated data being sent in the request if the request is put or patch
 * @param {Object} param0.record - The current record data being sent in the request if the request is put or patch
 * @param {Object} param0.meta - The HTTP meta elements
 * @returns {import('axios').AxiosPromise} - A promise containing the result of the request
 */
function process({ url, params, method, type, data, changes, record, meta, headers: extraHeaders }) {
  let headers = {
    'Accept': 'application/vnd.api+json',
    'Content-Type': 'application/vnd.api+json'
  }

  if (store.getters['sessions/isSignedIn']) {
    headers = { ...headers, ...store.getters['sessions/authorizationHeader'] }
  }

  if (extraHeaders) {
    headers = { ...headers, ...extraHeaders }
  }

  const willHaveBody = includes(['post', 'put', 'patch', 'delete'], method.toLowerCase())

  return axios({
    method,
    url,
    params,
    transformRequest(requestData) {
      if (requestData === DEFER) {
        return bodyProcessors[method.toLowerCase()]({ type, data, meta, changes, record })
      }

      return requestData
    },
    data: willHaveBody ? DEFER : data,
    headers
  })
}

export default {
  accept: async () => true,
  process
}
